import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from './api';
import { StatCard, MiniBar, Spinner, ErrorBox, Badge, fmtBytes, fmtUptime } from './components';

const MAX_HISTORY = 30;

const REFRESH_OPTIONS = [
  { label: '5s',  value: 5000 },
  { label: '10s', value: 10000 },
  { label: '15s', value: 15000 },
  { label: '30s', value: 30000 },
  { label: '60s', value: 60000 },
  { label: 'Off', value: 0 },
];

function push(arr, val) {
  const next = [...arr, val];
  return next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
}

/* ── Sections ────────────────────────────────────────────────────────────────*/
function SystemSection({ data }) {
  const { hostname, os, kernel, architecture, uptime_secs, load_avg, cpu, ram, gpu } = data;
  return (
    <>
      <p className="section-title">SYSTEM</p>
      <div className="detail-grid">
        <StatCard title="HOSTNAME"   value={hostname} />
        <StatCard title="OS"         value={os?.split(' ').slice(0,3).join(' ')} sub={kernel} />
        <StatCard title="ARCH"       value={architecture} />
        <StatCard title="UPTIME"     value={fmtUptime(uptime_secs)} />
        <StatCard title="LOAD AVG"
          value={load_avg?.map(v => v.toFixed(2)).join('  ')}
          sub={`${cpu?.cores_logical} logical cores`} />
        <StatCard title="CPU"
          value={cpu?.model?.split(' ').slice(0,4).join(' ')}
          sub={`${cpu?.cores_physical}P / ${cpu?.cores_logical}L  ·  ${cpu?.frequency_mhz} MHz`} />
        <StatCard title="RAM"
          value={`${ram?.used_mb ? (ram.used_mb / 1024).toFixed(1) : '—'} GB`}
          sub={`of ${ram?.total_mb ? (ram.total_mb / 1024).toFixed(1) : '—'} GB`}
          pct={ram?.percent} />
        <StatCard title="SWAP"
          value={`${ram?.swap_used_mb ? (ram.swap_used_mb / 1024).toFixed(1) : '—'} GB`}
          pct={ram?.swap_percent} />
      </div>
      {gpu?.length > 0 && (
        <>
          <p className="section-title">GPU</p>
          <div className="detail-grid">
            {gpu.map((g, i) => (
              <StatCard key={i} title={`GPU ${i} · ${g.vendor}`} value={g.name}>
                <div style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 12 }}>
                  <div>Temp: {g.temperature_c ?? '—'} °C</div>
                  <div>Util: {g.utilization_pct ?? '—'}%</div>
                  {g.memory_total_mb && (
                    <div>VRAM: {g.memory_used_mb?.toFixed(0)} / {g.memory_total_mb?.toFixed(0)} MB</div>
                  )}
                </div>
              </StatCard>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function TempSection({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return null;
  return (
    <>
      <p className="section-title">TEMPERATURES</p>
      <div className="detail-grid">
        {entries.map(([label, v]) => {
          const t = v.temp_c;
          const cls = t > 85 ? 'danger' : t > 70 ? 'warn' : 'ok';
          return (
            <StatCard key={label} title={label.split('/').pop()}>
              <div className={`card-value ${cls}`}>{t?.toFixed(1)} °C</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                {label.split('/')[0]}
              </div>
            </StatCard>
          );
        })}
      </div>
    </>
  );
}

function NetworkSection({ data }) {
  const entries = Object.entries(data || {});
  return (
    <>
      <p className="section-title">NETWORK</p>
      <div className="detail-wide">
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>INTERFACE</th><th>IP</th>
                <th>↓ RECV/s</th><th>↑ SENT/s</th>
                <th>TOTAL ↓</th><th>TOTAL ↑</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([nic, v]) => (
                <tr key={nic}>
                  <td className="mono">{nic}</td>
                  <td className="mono">{v.ip || '—'}</td>
                  <td className="mono">{fmtBytes(v.bytes_recv_per_sec)}/s</td>
                  <td className="mono">{fmtBytes(v.bytes_sent_per_sec)}/s</td>
                  <td className="mono">{fmtBytes(v.total_bytes_recv)}</td>
                  <td className="mono">{fmtBytes(v.total_bytes_sent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function DisksSection({ usage, io }) {
  return (
    <>
      <p className="section-title">DISKS</p>
      <div className="detail-grid">
        {(usage || []).map(d => (
          <StatCard key={d.mountpoint} title={d.mountpoint}
            sub={`${d.device} · ${d.fstype}`}
            pct={d.percent}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 13, marginTop: 6 }}>
              {d.used_gb} GB / {d.total_gb} GB
            </div>
          </StatCard>
        ))}
      </div>
      {io && Object.keys(io).length > 0 && (
        <div className="detail-wide" style={{ marginTop: 16 }}>
          <div className="card">
            <div className="card-title">DISK I/O</div>
            <table className="tbl">
              <thead>
                <tr><th>DEVICE</th><th>READ/s</th><th>WRITE/s</th><th>OPS R</th><th>OPS W</th></tr>
              </thead>
              <tbody>
                {Object.entries(io).map(([dev, v]) => (
                  <tr key={dev}>
                    <td className="mono">{dev}</td>
                    <td className="mono">{fmtBytes(v.read_bytes_per_sec)}/s</td>
                    <td className="mono">{fmtBytes(v.write_bytes_per_sec)}/s</td>
                    <td className="mono">{v.read_count_per_sec}/s</td>
                    <td className="mono">{v.write_count_per_sec}/s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function DockerSection({ data }) {
  if (!data?.length) return (
    <>
      <p className="section-title">DOCKER</p>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', padding: 16 }}>
        No running containers.
      </div>
    </>
  );
  return (
    <>
      <p className="section-title">DOCKER ({data.length})</p>
      <div className="detail-wide">
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>NAME</th><th>IMAGE</th><th>STATUS</th>
                <th>CPU%</th><th>MEM</th><th>MEM%</th>
                <th>NET ↓/↑</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => (
                <tr key={c.id}>
                  <td className="mono">{c.name}</td>
                  <td className="mono muted" style={{ fontSize: 11 }}>{c.image}</td>
                  <td>
                    <Badge label={c.status}
                      color={c.status === 'running' ? 'var(--ok)' : 'var(--warn)'} />
                  </td>
                  <td className="mono">{c.cpu_pct}%</td>
                  <td className="mono">{c.mem_usage_mb} MB</td>
                  <td className="mono">
                    <span className={c.mem_pct > 80 ? 'danger' : c.mem_pct > 60 ? 'warn' : ''}>
                      {c.mem_pct}%
                    </span>
                  </td>
                  <td className="mono">{fmtBytes(c.net_rx_bytes)} / {fmtBytes(c.net_tx_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ProcessSection({ data }) {
  return (
    <>
      <p className="section-title">TOP PROCESSES (CPU)</p>
      <div className="detail-wide">
        <div className="card">
          <table className="tbl">
            <thead>
              <tr><th>PID</th><th>NAME</th><th>USER</th><th>CPU%</th><th>MEM%</th><th>STATUS</th><th>CMD</th></tr>
            </thead>
            <tbody>
              {(data || []).map(p => (
                <tr key={p.pid}>
                  <td className="mono">{p.pid}</td>
                  <td className="mono">{p.name}</td>
                  <td className="mono muted">{p.user}</td>
                  <td className="mono">
                    <span className={p.cpu_pct > 80 ? 'danger' : p.cpu_pct > 40 ? 'warn' : ''}>
                      {p.cpu_pct}%
                    </span>
                  </td>
                  <td className="mono">{p.mem_pct}%</td>
                  <td className="mono muted" style={{ fontSize: 11 }}>{p.status}</td>
                  <td className="mono muted" style={{ fontSize: 11, maxWidth: 240 }}>{p.cmdline}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ── Charts ──────────────────────────────────────────────────────────────────*/
function HistoryChart({ history, keys, colors, unit = '' }) {
  return (
    <ResponsiveContainer width="100%" height={140}>
      <LineChart data={history} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <XAxis dataKey="t" hide />
        <YAxis domain={[0, 100]} tick={{ fontFamily: 'var(--mono)', fontSize: 10, fill: 'var(--muted)' }} />
        <Tooltip
          contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)',
                          fontFamily: 'var(--mono)', fontSize: 11 }}
          formatter={v => [`${v.toFixed(1)}${unit}`]}
          labelFormatter={() => ''} />
        {keys.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} dot={false} isAnimationActive={false}
                stroke={colors[i] || 'var(--accent)'} strokeWidth={1.5} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ── Main ────────────────────────────────────────────────────────────────────*/
const TABS = ['OVERVIEW', 'NETWORK', 'DISKS', 'DOCKER', 'PROCESSES'];

export default function HostDetailPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setL]     = useState(true);
  const [error, setErr]     = useState('');
  const [tab, setTab]       = useState('OVERVIEW');
  const [cpuHist, setCpu]   = useState([]);
  const [ramHist, setRam]   = useState([]);
  const [refresh, setRefresh] = useState(10000);

  const goBack = () => {
    // Use browser history if available, otherwise go to overview
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hosts/${id}/metrics`);
      const d = r.data;
      setData(d);
      const t = new Date().toLocaleTimeString('en', { hour12: false });
      const sys = d.system;
      const cpuPct = sys.load_avg
        ? Math.min(sys.load_avg[0] / (sys.cpu?.cores_logical || 1) * 100, 100)
        : 0;
      setCpu(prev => push(prev, { t, cpu: cpuPct }));
      setRam(prev => push(prev, { t, ram: sys.ram?.percent || 0 }));
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load metrics');
    } finally { setL(false); }
  }, [id]);

  useEffect(() => {
    load();
    if (!refresh) return;
    const t = setInterval(load, refresh);
    return () => clearInterval(t);
  }, [load, refresh]);

  if (loading) return (
    <div className="detail-wrap">
      <div className="detail-header" style={{ marginBottom: 24 }}>
        <button className="detail-back" onClick={goBack}>← BACK</button>
      </div>
      <Spinner />
    </div>
  );

  if (error) return (
    <div className="detail-wrap">
      <div className="detail-header" style={{ marginBottom: 24 }}>
        <button className="detail-back" onClick={goBack}>← BACK</button>
      </div>
      <ErrorBox msg={error} />
    </div>
  );

  if (!data) return null;

  const { system, temperatures, network, disks, disk_io, docker, top_processes } = data;

  return (
    <div className="detail-wrap">
      <div className="detail-header">
        <button className="detail-back" onClick={goBack}>← BACK</button>
        <div>
          <h1>{system.hostname}</h1>
          <div className="sub">{system.os} &nbsp;·&nbsp; up {fmtUptime(system.uptime_secs)}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>REFRESH</span>
            <div style={{ display: 'flex', gap: 0 }}>
              {REFRESH_OPTIONS.map(o => (
                <button key={o.value}
                  style={{
                    background: refresh === o.value ? 'var(--border)' : 'none',
                    border: '1px solid var(--border)',
                    borderLeft: o.value === REFRESH_OPTIONS[0].value ? '1px solid var(--border)' : 'none',
                    color: refresh === o.value ? 'var(--accent)' : 'var(--muted)',
                    padding: '5px 10px',
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    cursor: 'pointer',
                    transition: 'color 0.15s, background 0.15s',
                  }}
                  onClick={() => setRefresh(o.value)}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11,
                        color: refresh === 0 ? 'var(--muted)' : 'var(--ok)' }}>
            {refresh === 0 ? '○ PAUSED' : '● LIVE'}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="detail-grid">
        <StatCard title="CPU LOAD (1m)"
          value={`${(system.load_avg?.[0] / (system.cpu?.cores_logical || 1) * 100).toFixed(1)}%`}
          pct={system.load_avg?.[0] / (system.cpu?.cores_logical || 1) * 100}>
          <div style={{ marginTop: 12 }}>
            <HistoryChart history={cpuHist} keys={['cpu']} colors={['var(--accent)']} unit="%" />
          </div>
        </StatCard>
        <StatCard title="RAM"
          value={`${system.ram?.percent}%`}
          sub={`${(system.ram?.used_mb/1024).toFixed(1)} / ${(system.ram?.total_mb/1024).toFixed(1)} GB`}
          pct={system.ram?.percent}>
          <div style={{ marginTop: 12 }}>
            <HistoryChart history={ramHist} keys={['ram']} colors={['var(--accent)']} unit="%" />
          </div>
        </StatCard>
      </div>

      <div className="tabs">
        {TABS.map(t => (
          <button key={t} className={`tab${tab === t ? ' active' : ''}`}
                  onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'OVERVIEW'   && <SystemSection data={system} />}
      {tab === 'OVERVIEW'   && <TempSection data={temperatures} />}
      {tab === 'NETWORK'    && <NetworkSection data={network} />}
      {tab === 'DISKS'      && <DisksSection usage={disks} io={disk_io} />}
      {tab === 'DOCKER'     && <DockerSection data={docker} />}
      {tab === 'PROCESSES'  && <ProcessSection data={top_processes} />}
    </div>
  );
}
